// contexts/AuthContext.js
import React, {createContext, useCallback, useEffect, useState} from 'react';
import {axiosInstance, clearAuthHeader, initToken} from "config/axiosConfig";
import {postData} from "utils/TransactionUtil";

import Loading from "config/Loading";

// Context 생성
export const AuthContext = createContext();

let globalResetFunc = () => {};
export const setGlobalResetFunc = (fn) => {
  globalResetFunc = fn;
};
export const callGlobalResetFunc = () => {
  if (typeof globalResetFunc === 'function') {
    globalResetFunc();
  }
};

// Context의 Provider 컴포넌트
export const AuthProvider = (props) => {

    const [isLoggedIn, setIsLoggedIn] = useState(null); // 로그인 상태
    const [userInfo, setUserInfo] = useState(null); // 사용자 정보
    const [loading, setLoading] = useState(true); // 로딩 상태 관리
    const [menuList, setMenuList] = useState(() => {});


    /* = 자동 로그인 처리 ================================================*/
    useEffect(() => {
        const checkLoginStatus = async () => {
            try {
                await initToken(); // 로컬스토리지에서 토큰을 가져와 해더에 셋팅

                // 해더에 토큰 여부 체크 > 토큰을 이용하여 유저 정보를 가져옴
                if (axiosInstance.defaults.headers.common.Authorization) {
                    const data = await postData("/getUserInfo");
                    if (data) {
                        login(data); // 권한 컨텍스트 로그인 처리
                    } else {
                        console.log("아이디와 비밀번호를 확인해 주세요.");
                    }
                }
            } catch (error) {
                console.error("자동 로그인 오류:", error);
            } finally {
                setLoading(false); // 로딩 종료
            }
        };

        checkLoginStatus();
    }, []);


    /* = 로그인 관련 ================================================*/
    const login = useCallback((userData) => {
        setIsLoggedIn(true);
        setUserInfo(userData.userInfo);

        setMenuList(userData.menuList); // menuList 상태 업데이트
        sessionStorage.setItem("menuList", JSON.stringify(userData.menuList)); // 사용자의 맞는 메뉴 정보

        timeReset(); // 타임 아웃 정보 초기화
    }, []);

    // 로그아웃
    const logout = useCallback((showAlert = false) => {
        setIsLoggedIn(false);
        setUserInfo(null);
        clearAuthHeader(); // 헤더 초기화
        localStorage.clear(); // 로컬 스토리지 초기화
        sessionStorage.clear(); // 세션 스토리지 초기화

        if (showAlert) {
            alert('로그인이 만료되었습니다.');
            window.location.href = '/'; // 메인 페이지로 리다이렉트
        }
    }, []);


    /* = 로그아웃 타이머 관련 설정 ================================================*/
    const initialTime = 60 * 60; // 카운트다운 초기값 (60분)
    const storedTimeLeft = localStorage.getItem('timeLeft');
    const [timeLeft, setTimeLeft] = useState(storedTimeLeft ? parseInt(storedTimeLeft, 10) : initialTime);

    // 로그인 기준 시간을 초기화 함. (60분)
    const timeReset = () => {
        setTimeLeft(initialTime);
        localStorage.setItem('timeLeft', initialTime); // 타이머 초기값 로컬 스토리지에 저장
    };

    // 시간이 1초마다 감소
    // 로그인 되면 카운트가 동작 한다.
    useEffect(() => {

        if (!isLoggedIn) return;

        const interval = setInterval(() => {
            setTimeLeft((prevTime) => {
                if (prevTime <= 1) {
                    clearInterval(interval);
                    logout(true); // 자동 로그아웃 시 alert 표시
                    return 0;
                }
                const newTime = prevTime - 1;
                localStorage.setItem('timeLeft', newTime); // 남은 시간 로컬 스토리지에 저장
                return newTime;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [isLoggedIn, logout]);

    // 앱 초기 실행 시 전역 함수로 등록
    setGlobalResetFunc(timeReset);

    // Context의 값을 제공할 Provider를 반환
    return (
        <AuthContext.Provider
            value={{
                isLoggedIn,
                timeLeft,
                userInfo,
                menuList,
                onLogin: login,
                onLogout: logout,
                onTimeReset: timeReset
            }}
        >
            {loading ? <Loading /> : props.children}
        </AuthContext.Provider>
    );
};
